import {
  BiologicalParentData,
  ClinicalData,
  ConsentData,
  DEFAULT_FATHER_DATA,
  DEFAULT_FORM_DATA,
  DEFAULT_MOTHER_DATA,
  FamilyHistoryData,
  OrderData,
  PatientData,
  ReasonForTestingData,
  RequisitionFormData,
} from '@/types/RequisitionFormTypes'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

type NavigationSection =
  | 'order-type'
  | 'patient-information'
  | 'clinical-information'
  | 'reason-for-testing'
  | 'family-history'
  | 'biological-parent-information-1'
  | 'biological-parent-information-2'
  | 'consent'
  | 'review-submit'

interface FormState {
  formData: RequisitionFormData
  currentSection: NavigationSection
  collapsedSections: Record<string, boolean>
  isDirty: boolean
  lastSaved: string | null
  isFormInitialized: boolean
}

const initialState: FormState = {
  formData: DEFAULT_FORM_DATA,
  currentSection: 'order-type',
  collapsedSections: {},
  isDirty: false,
  lastSaved: null,
  isFormInitialized: false,
}

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    // Form data updates
    updateOrderData: (state, action: PayloadAction<Partial<OrderData>>) => {
      state.formData.order = { ...state.formData.order, ...action.payload }
      state.isDirty = true
    },
    updatePatientData: (state, action: PayloadAction<Partial<PatientData>>) => {
      state.formData.patient = { ...state.formData.patient, ...action.payload }
      state.isDirty = true
    },
    updateClinicalData: (state, action: PayloadAction<Partial<ClinicalData>>) => {
      state.formData.clinical = { ...state.formData.clinical, ...action.payload }
      state.isDirty = true
    },
    updateReasonData: (state, action: PayloadAction<Partial<ReasonForTestingData>>) => {
      state.formData.reason = { ...state.formData.reason, ...action.payload }
      state.isDirty = true
    },
    updateFamilyData: (state, action: PayloadAction<Partial<FamilyHistoryData>>) => {
      state.formData.family = { ...state.formData.family, ...action.payload }
      state.isDirty = true
    },
    updateMotherData: (state, action: PayloadAction<Partial<BiologicalParentData>>) => {
      state.formData.mother = { ...DEFAULT_MOTHER_DATA, ...state.formData.mother, ...action.payload }
      state.isDirty = true
    },
    updateFatherData: (state, action: PayloadAction<Partial<BiologicalParentData>>) => {
      state.formData.father = { ...DEFAULT_FATHER_DATA, ...state.formData.father, ...action.payload }
      state.isDirty = true
    },
    updateConsentData: (state, action: PayloadAction<Partial<ConsentData>>) => {
      state.formData.consent = { ...state.formData.consent, ...action.payload }
      state.isDirty = true
    },
    updateConsentFormData: (
      state,
      action: PayloadAction<
        Partial<{
          medicalProfessionalName: string
          npi: string
          signatureDate: string
          signature: string
        }>
      >
    ) => {
      // Store consent form data in a separate field or extend the consent data
      if (!state.formData.consentFormData) {
        state.formData.consentFormData = {
          medicalProfessionalName: '',
          npi: '',
          signatureDate: '',
          signature: '',
        }
      }
      state.formData.consentFormData = { ...state.formData.consentFormData, ...action.payload }
      state.isDirty = true
    },
    clearMotherData: (state) => {
      state.formData.mother = DEFAULT_MOTHER_DATA
      state.isDirty = true
    },
    clearFatherData: (state) => {
      state.formData.father = DEFAULT_FATHER_DATA
      state.isDirty = true
    },
    clearParentalData: (state) => {
      state.formData.mother = DEFAULT_MOTHER_DATA
      state.formData.father = DEFAULT_FATHER_DATA
      state.isDirty = true
    },

    // Complete form data replacement
    setFormData: (state, action: PayloadAction<RequisitionFormData>) => {
      state.formData = action.payload
      state.isDirty = false
      state.lastSaved = new Date().toISOString()
      state.isFormInitialized = true
    },

    // Load existing request data from JSON payload
    loadExistingRequestData: (state, action: PayloadAction<RequisitionFormData>) => {
      state.formData = action.payload
      state.isDirty = false
      state.lastSaved = new Date().toISOString()
      state.isFormInitialized = true
    },

    // Reset form to default
    resetForm: (state) => {
      state.formData = DEFAULT_FORM_DATA
      state.isDirty = false
      state.lastSaved = null
      state.isFormInitialized = false
    },

    // UI state management
    setCurrentSection: (state, action: PayloadAction<NavigationSection>) => {
      state.currentSection = action.payload
    },
    toggleSectionCollapse: (state, action: PayloadAction<string>) => {
      const sectionId = action.payload
      state.collapsedSections[sectionId] = !state.collapsedSections[sectionId]
    },
    setSectionCollapsed: (state, action: PayloadAction<{ sectionId: string; collapsed: boolean }>) => {
      const { sectionId, collapsed } = action.payload
      state.collapsedSections[sectionId] = collapsed
    },

    // Form state management
    markAsSaved: (state) => {
      state.isDirty = false
      state.lastSaved = new Date().toISOString()
    },
    markAsDirty: (state) => {
      state.isDirty = true
    },
  },
})

export const {
  updateOrderData,
  updatePatientData,
  updateClinicalData,
  updateReasonData,
  updateFamilyData,
  updateMotherData,
  updateFatherData,
  updateConsentData,
  updateConsentFormData,
  clearMotherData,
  clearFatherData,
  clearParentalData,
  setFormData,
  loadExistingRequestData,
  resetForm,
  setCurrentSection,
  toggleSectionCollapse,
  setSectionCollapsed,
  markAsSaved,
  markAsDirty,
} = formSlice.actions

export default formSlice.reducer
